/**
 * FlavorBadge — MCP connection "Flavor" badge (bug #6, UI-SPEC v2.1)
 *
 * UI-SPEC v2.1 distinguishes two MCP install *flavors*:
 *   - Marketplace (slate) — installed from a published system_mcp_servers
 *     catalog entry. These carry a non-null `systemServerId`.
 *   - External (indigo)   — a directly-registered Tier-2/3 server with no
 *     marketplace lineage (`systemServerId` is null).
 *
 * The backend already returns `systemServerId` per server; the list just
 * never distinguished Flavor 1 from Flavor 2. This badge + the
 * All/Marketplace/External filter close that spec-vs-impl gap.
 *
 * NOTE: every install in the current data model joins through
 * system_mcp_servers (see MCP_JOIN_SELECT), so `systemServerId` is
 * effectively always present today. `serverFlavor` is written to honour the
 * spec semantics regardless, so when directly-registered (systemServerId
 * NULL) installs land, the External flavor renders without further changes.
 */

import { Store, Network } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { McpServer } from '@/lib/mock-connections-data';

export type ConnectionFlavor = 'marketplace' | 'external';

export const FLAVOR_LABELS: Record<ConnectionFlavor, string> = {
  marketplace: 'Marketplace',
  external: 'External',
};

export const FLAVOR_BADGE_STYLES: Record<ConnectionFlavor, string> = {
  marketplace:
    'border-slate-400/30 text-slate-600 dark:text-slate-300',
  external:
    'border-indigo-500/30 text-indigo-600 dark:text-indigo-400',
};

const FLAVOR_ICONS: Record<ConnectionFlavor, typeof Store> = {
  marketplace: Store,
  external: Network,
};

/**
 * Derive a server's flavor from its marketplace lineage. Pure + exported
 * so the list filter + a unit test can classify without rendering.
 */
export function serverFlavor(
  s: Pick<McpServer, 'systemServerId'>,
): ConnectionFlavor {
  return s.systemServerId ? 'marketplace' : 'external';
}

interface FlavorBadgeProps {
  flavor: ConnectionFlavor;
  className?: string;
}

export function FlavorBadge({ flavor, className }: FlavorBadgeProps) {
  const Icon = FLAVOR_ICONS[flavor];
  return (
    <Badge
      variant="outline"
      className={cn('gap-0.5 text-[9px]', FLAVOR_BADGE_STYLES[flavor], className)}
      title={`Flavor: ${FLAVOR_LABELS[flavor]}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {FLAVOR_LABELS[flavor]}
    </Badge>
  );
}
